'use client'

import { AnimatePresence, motion } from 'framer-motion'
import type { SVGAttributes } from 'react'
import { useEffect, useState } from 'react'

import { useSierra } from '@src/components/Sierra/SierraProvider'
import { getBreakpoints } from '@src/utils/breakpoints.util'
import { EXPERIMENTS } from '@src/utils/constants.util'
import { useExperimentContext } from '@src/utils/experimentProvider'
import { storage } from '@src/utils/storage.util'

interface SierraProps {
  ctaText?: string
}

const proactiveBanner = {
  text: "👋 Hi, I'm a virtual agent. How can I help?",
  showAfterSeconds: 3,
  hideAfterSeconds: 10,
  cooldownMinutes: 30,
}

const LAST_BANNER_SHOWN_KEY = 'sierra-last-banner-shown'

export function Sierra(props: SierraProps) {
  const { sierra } = useSierra()
  const { allVariants, experimentClient } = useExperimentContext() ?? {}
  const [isProactiveBannerOpen, setIsProactiveBannerOpen] = useState(false)
  const isInSierraTest = allVariants?.[EXPERIMENTS.sierra]?.value === 'variant'
  const { isMobile } = getBreakpoints()

  useEffect(() => {
    if (!sierra || isMobile) return

    const shouldShowBanner = () => {
      const lastShown = storage.session.get(LAST_BANNER_SHOWN_KEY)
      if (!lastShown) {
        return true
      }
      const lastShownTime = new Date(lastShown).getTime()
      const currentTime = new Date().getTime()
      const cooldownMs = proactiveBanner.cooldownMinutes * 60 * 1000

      return currentTime - lastShownTime > cooldownMs
    }

    if (shouldShowBanner()) {
      const showTimer = setTimeout(() => {
        setIsProactiveBannerOpen(true)
        storage.session.set(LAST_BANNER_SHOWN_KEY, new Date().toISOString())

        const hideTimer = setTimeout(() => {
          setIsProactiveBannerOpen(false)
        }, proactiveBanner.hideAfterSeconds * 1000)

        return () => clearTimeout(hideTimer)
      }, proactiveBanner.showAfterSeconds * 1000)

      return () => clearTimeout(showTimer)
    }

    experimentClient?.exposure(EXPERIMENTS.sierra)
  }, [experimentClient, sierra, isMobile])

  return sierra && isInSierraTest ? (
    <div className="fixed bottom-32 right-4 z-10 hidden flex-col items-end lg:flex">
      <AnimatePresence>
        {isProactiveBannerOpen && (
          <motion.p
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: '0%' }}
            exit={{ opacity: 0, y: '100%' }}
            className="mb-2 rounded-full bg-bodi-cool-gray-700 bg-clip-padding px-4 py-2 text-bodi-white shadow-lg"
          >
            {proactiveBanner.text}
          </motion.p>
        )}
      </AnimatePresence>
      <AnimatePresence>
        <motion.button
          initial={{ opacity: 0, y: '100%' }}
          animate={{ opacity: 1, y: '0%' }}
          exit={{ opacity: 0, y: '100%' }}
          className="relative z-10 flex items-center justify-center rounded-full bg-bodi-cool-gray-700 bg-clip-padding px-4 py-2 text-body-base font-medium text-bodi-white shadow-lg ring-bodi-green-bright-500 hover:bg-bodi-black focus:bg-bodi-black focus:ring-1"
          onClick={() => {
            sierra.openChatModal()
          }}
        >
          <ChatIcon className="mr-1" />
          {props.ctaText ?? 'Chat'}
        </motion.button>
      </AnimatePresence>
    </div>
  ) : null
}

function ChatIcon(props: SVGAttributes<SVGElement>) {
  return (
    <svg
      width="20"
      height="20"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      aria-hidden="true"
      stroke="none"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#FFFFFF"
        d="M17.4977 9.29362C17.4992 9.35647 17.5 9.41952 17.5 9.48276C17.5 13.9576 13.6259 17.4655 9 17.4655C8.19568 17.4655 7.41572 17.3604 6.67562 17.1633C5.59534 17.5147 4.41192 17.8303 3.47985 18.0624C2.96809 18.1898 2.52723 18.2933 2.21386 18.365C2.05711 18.4009 1.93207 18.4288 1.84579 18.4479L1.74628 18.4698L1.71995 18.4755L1.71011 18.4776C1.29825 18.5661 0.8751 18.3871 0.6519 18.0298C0.428706 17.6725 0.453337 17.2137 0.7135 16.8824C1.22982 16.2248 1.71458 15.2068 2.05041 14.081C1.07772 12.7861 0.5 11.1999 0.5 9.48276C0.5 5.0079 4.37408 1.5 9 1.5C9.684 1.5 10.3515 1.57669 10.9925 1.72185C10.7205 2.31878 10.5521 2.97285 10.5103 3.66111C10.0274 3.55588 9.522 3.5 9 3.5C5.34165 3.5 2.5 6.24467 2.5 9.48276C2.5 10.8809 3.02071 12.1725 3.90747 13.2C4.12082 13.4472 4.20004 13.783 4.11966 14.0995C3.9567 14.7411 3.74352 15.3869 3.48781 15.9973C4.38869 15.7653 5.42414 15.4771 6.33352 15.1697C6.53194 15.1026 6.74642 15.0995 6.94669 15.1609C7.59042 15.3581 8.28044 15.4655 9 15.4655C12.6584 15.4655 15.5 12.7208 15.5 9.48276V9.47758C15.6647 9.49242 15.8315 9.5 16 9.5C16.5192 9.5 17.0215 9.42807 17.4977 9.29362Z"
      ></path>
      <path
        d="M16 7.5C17.933 7.5 19.5 5.933 19.5 4C19.5 2.067 17.933 0.5 16 0.5C14.067 0.5 12.5 2.067 12.5 4C12.5 5.933 14.067 7.5 16 7.5Z"
        fill="#BDF987"
      ></path>
    </svg>
  )
}
